import React from "react";
import moment from "moment";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, find } from "lodash";
import { getMetaValue, getInitials, detectFieldChanges } from "utils/typeUtils";

import ComparisonCard from "./ComparisonCard";
import AccountHolderInfoCard from "./AccountHolderInfo";

// Mirrors the approval-request selection consumed here (see GetApprovalRequestQuery
// in types/graphql). Only the fields this component reads are modeled; the parent
// (ApprovalDocumentItem) passes the full object. PropTypes had `approval`/`user`
// as optional `.object`.
interface MetadataItem {
  name?: string | null;
  value?: unknown;
}

interface Approval {
  correlationName?: string | null;
  metadataItems?: (MetadataItem | null)[] | null;
}

// `user` is consumed entirely through loose lodash `get`, except `user.accounts`
// which is read directly and passed to `find` with an `{ accountType }` shorthand
// predicate — so model accounts as loose records to keep that shorthand valid.
interface User {
  accounts?: Record<string, unknown>[];
}

interface OwnProps {
  approval?: Approval;
  user?: User;
}

type Props = WithApolloClient<OwnProps>;

class NameChangeApproval extends React.PureComponent<Props> {
  render() {
    const metadataItems = get(
      this.props.approval,
      "metadataItems",
      []
    ) as MetadataItem[];

    // getMetaValue returns the metadata `value` as `unknown`; at runtime these
    // are strings. Narrow at the source, matching the annotated-sibling idiom.
    const currentNameObj = {
      firstName: getMetaValue(metadataItems, "Current First Name") as
        | string
        | null,
      lastName: getMetaValue(metadataItems, "Current Last Name") as
        | string
        | null,
    };

    const newNameObj = {
      firstName: getMetaValue(metadataItems, "New First Name") as string | null,
      // found-not-fixed: looks up "New LastName" (no space) while the current
      // counterpart uses "Current Last Name"; both camelCase to "newLastName"/
      // "currentLastName" via getMetaValue so it still resolves — preserved verbatim.
      lastName: getMetaValue(metadataItems, "New LastName") as string | null,
    };

    const fields = {
      firstName: "First Name",
      lastName: "Last Name",
    };
    const nameChanges = detectFieldChanges(currentNameObj, newNameObj, fields);
    const firstName = getMetaValue(metadataItems, "Current First Name") as
      | string
      | null;
    const lastName = getMetaValue(metadataItems, "Current Last Name") as
      | string
      | null;
    const submissionDate = getMetaValue(metadataItems, "Submission Date") as
      | string
      | null;
    const apexAccount = getMetaValue(metadataItems, "Apex Account Number") as
      | string
      | null;
    const dob = get(this.props.user, "userProfile.dob");
    const ssn = get(this.props.user, "fullSsn");
    const email = get(this.props.approval, "correlationName", null);
    const kycState = get(this.props.user, "user.state");
    const silaWalletID = get(
      // `user` is optional but read directly here — `user!` preserves the
      // original throw-on-undefined behavior (lodash find(undefined) would not).
      find(this.props.user!.accounts, { accountType: "WALLET" }),
      "accountNumber",
      null
    );
    const initials = getInitials(firstName, lastName);
    const phone = get(this.props.user, "userProfile.phoneNumber");

    const formattedDate = submissionDate
      ? moment(submissionDate).format("dddd, MMMM Do YYYY")
      : null;

    return (
      <>
        <AccountHolderInfoCard
          initials={initials}
          firstName={firstName}
          lastName={lastName}
          email={email}
          dob={dob}
          ssn={ssn}
          userId={get(this.props.approval, "entityIdentifier.id", "N/A")}
          submissionDate={formattedDate}
          apexAccount={apexAccount}
          kycState={kycState}
          kycStatus={kycState}
          silaWalletID={silaWalletID as React.ReactNode}
          phone={phone}
        />

        <ComparisonCard
          title="Name Comparison"
          changeType="name"
          currentLabel="Current On-File Name"
          newChangeLabel="New Name"
          currentFirst={currentNameObj.firstName}
          currentLast={currentNameObj.lastName}
          newFirst={newNameObj.firstName}
          newLast={newNameObj.lastName}
          changes={nameChanges}
        />
      </>
    );
  }
}

export default withApollo<Props>(NameChangeApproval);
